import React, { useEffect, useRef, useState } from "react";
import { FeedbackThumbsType } from "../../types";
import { useHttpErrorHandler, usePostFeedback } from "../../hooks";
import clsx from "clsx";
import { Button, Container } from "@bolt/components";
import { ThumbDownIcon, ThumbUpIcon } from "../Icons";
import "./ChatActionsStyles.css";
import { CopyToClipboard } from "../CopyToClipboard";

type ChatActionsProps = {
  questionId: string;
  textToCopy: string;
  questionTimestamp: string;
};

export const ChatMessageActions = ({ questionId, textToCopy, questionTimestamp }: ChatActionsProps) => {
  const [userFeedback, setUserFeedback] = useState<string>("");
  const [showUserFeedbackInput, setShowUserFeedbackInput] = useState<boolean>(false);
  const [selected, setSelected] = useState<"" | FeedbackThumbsType>("");
  const [submitted, setSubmitted] = useState<boolean>(false);
  const feedbackEndRef = useRef<HTMLDivElement>(null);
  const { fetch: postFeedback } = usePostFeedback();
  const { handleHttpError } = useHttpErrorHandler();

  useEffect(() => {
    if (showUserFeedbackInput) {
      feedbackEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [showUserFeedbackInput]);

  const handleSubmitFeedback = () => {
    if (selected === "") {
      throw new Error("The thumbs need to be up or down here.");
    }

    setShowUserFeedbackInput(false);

    postFeedback({
      body: {
        question_id: questionId,
        question_timestamp: questionTimestamp,
        user_rating: selected === "up" ? 1 : -1,
        user_comment: userFeedback,
      },
    })
      .then(() => {
        setSubmitted(true);
      })
      .catch((error) => {
        handleHttpError(error);
        setSelected("");
      });
  };

  const handleThumb = (upOrDown: FeedbackThumbsType) => {
    if (!submitted) {
      if (upOrDown === selected) {
        setSelected("");
        setShowUserFeedbackInput(false);
      } else {
        setSelected(upOrDown);
        setShowUserFeedbackInput(true);
      }
    }
  };

  return (
    <Container variant="ghost" padding="xs" direction="column" align="end">
      <Container variant="ghost" padding="xs" gap="xs" justify="end" className="feedback-container">
        <CopyToClipboard textToCopy={textToCopy} />
        <button onClick={() => handleThumb("up")} className={clsx("btn-link", selected === "up" && "up-btn-selected")}>
          <ThumbUpIcon />
        </button>
        <button
          onClick={() => handleThumb("down")}
          className={clsx("btn-link", selected === "down" && "down-btn-selected")}
        >
          <ThumbDownIcon />
        </button>
      </Container>
      {showUserFeedbackInput && (
        <Container variant="ghost" align="center" className="feedback-input-container">
          <textarea
            className="feedback-input"
            data-testid="feedback-input"
            draggable={false}
            placeholder="Please provide additional feedback (optional)"
            onChange={(e: any) => setUserFeedback(e.target.value)}
            value={userFeedback}
          />
          <Button onClick={handleSubmitFeedback} size="lg" intent="primary">
            submit
          </Button>
        </Container>
      )}
      <div ref={feedbackEndRef} />
    </Container>
  );
};
